import type {
  AffinityChartFilterType,
  AffinityChartNode,
  AffinityChartNodeFamily,
} from "@/features/affinity-chart/affinity-chart.types";

const NODE_ACCENTS: Record<AffinityChartFilterType, string> = {
  character: "#d07a35",
  creature: "#3b8d66",
  "item-object": "#2d6fa0",
  machine: "#6f5a91",
  culture: "#7e6947",
  faction: "#8655a9",
  location: "#2f7a61",
  magic_system: "#7054a6",
  political_system: "#8a6a31",
  race_species: "#6f6235",
  religion: "#9a4f67",
  style_guide: "#3d6886",
  technology: "#356692",
};

const NODE_ICON_MARKUP: Record<AffinityChartFilterType, string> = {
  character: [
    '<circle cx="32" cy="25" r="7" fill="none" stroke="#fffaf4" stroke-width="3" />',
    '<path d="M21 44c3-7 9-10 11-10s8 3 11 10" fill="none" stroke="#fffaf4" stroke-linecap="round" stroke-width="3" />',
  ].join(""),
  creature: [
    '<circle cx="23" cy="24" r="4" fill="#fffaf4" />',
    '<circle cx="31" cy="20" r="4" fill="#fffaf4" />',
    '<circle cx="40" cy="24" r="4" fill="#fffaf4" />',
    '<path d="M24 41c0-5 4-9 8-9s8 4 8 9c0 3-2 5-5 5-1 0-2 0-3-1-1 1-2 1-3 1-3 0-5-2-5-5Z" fill="#fffaf4" />',
  ].join(""),
  "item-object": [
    '<rect x="19" y="21" width="26" height="22" rx="4" fill="none" stroke="#fffaf4" stroke-width="3" />',
    '<path d="M19 29h26" fill="none" stroke="#fffaf4" stroke-width="3" />',
  ].join(""),
  machine: [
    '<circle cx="32" cy="32" r="8" fill="none" stroke="#fffaf4" stroke-width="3" />',
    '<path d="M32 17v6M32 41v6M17 32h6M41 32h6M21.5 21.5l4 4M38.5 38.5l4 4M21.5 42.5l4-4M38.5 25.5l4-4" fill="none" stroke="#fffaf4" stroke-linecap="round" stroke-width="3" />',
  ].join(""),
  culture: [
    '<circle cx="32" cy="32" r="15" fill="none" stroke="#fffaf4" stroke-width="3" />',
    '<path d="M17 32h30M32 17c4 5 6 10 6 15s-2 10-6 15M32 17c-4 5-6 10-6 15s2 10 6 15" fill="none" stroke="#fffaf4" stroke-linecap="round" stroke-width="2.5" />',
  ].join(""),
  faction: [
    '<path d="M32 17 43 22v10c0 8-5 13-11 16-6-3-11-8-11-16V22l11-5Z" fill="none" stroke="#fffaf4" stroke-linejoin="round" stroke-width="3" />',
  ].join(""),
  location: [
    '<path d="M32 46s11-10 11-19a11 11 0 1 0-22 0c0 9 11 19 11 19Z" fill="none" stroke="#fffaf4" stroke-linejoin="round" stroke-width="3" />',
    '<circle cx="32" cy="27" r="4" fill="#fffaf4" />',
  ].join(""),
  magic_system: [
    '<path d="m32 16 3.5 10.5L46 30l-10.5 3.5L32 44l-3.5-10.5L18 30l10.5-3.5L32 16Z" fill="none" stroke="#fffaf4" stroke-linejoin="round" stroke-width="3" />',
  ].join(""),
  political_system: [
    '<path d="M18 25h28M22 25v17M30 25v17M38 25v17M17 42h30M20 21l12-6 12 6" fill="none" stroke="#fffaf4" stroke-linecap="round" stroke-linejoin="round" stroke-width="3" />',
  ].join(""),
  race_species: [
    '<path d="M24 19c8 4 8 22 16 26M40 19c-8 4-8 22-16 26M25 25c2 1 4 1 7 0M32 32c3 1 5 1 7 0M25 39c2 1 4 1 7 0" fill="none" stroke="#fffaf4" stroke-linecap="round" stroke-width="3" />',
  ].join(""),
  religion: [
    '<path d="m32 17 4 9 9 4-9 4-4 9-4-9-9-4 9-4 4-9Z" fill="none" stroke="#fffaf4" stroke-linejoin="round" stroke-width="3" />',
  ].join(""),
  style_guide: [
    '<path d="M23 17h15l7 7v23H23z" fill="none" stroke="#fffaf4" stroke-linejoin="round" stroke-width="3" />',
    '<path d="M38 17v8h7M28 31h12M28 37h12" fill="none" stroke="#fffaf4" stroke-linecap="round" stroke-width="3" />',
  ].join(""),
  technology: [
    '<circle cx="32" cy="32" r="7" fill="none" stroke="#fffaf4" stroke-width="3" />',
    '<path d="M32 18v5M32 41v5M18 32h5M41 32h5M22.5 22.5l3.5 3.5M38 38l3.5 3.5M22.5 41.5 26 38M38 26l3.5-3.5" fill="none" stroke="#fffaf4" stroke-linecap="round" stroke-width="3" />',
  ].join(""),
};

function buildShapeMarkup(family: AffinityChartNodeFamily, accent: string) {
  return family === "entity-card"
    ? `<circle cx="32" cy="32" r="24" fill="${accent}" />`
    : `<rect x="15" y="15" width="34" height="34" rx="8" transform="rotate(45 32 32)" fill="${accent}" />`;
}

export function getAffinityChartNodeAccent(type: AffinityChartFilterType) {
  return NODE_ACCENTS[type] ?? "#6f6254";
}

export function getAffinityChartNodePlaceholderDataUri(
  type: AffinityChartFilterType,
  family: AffinityChartNodeFamily,
) {
  const accent = getAffinityChartNodeAccent(type);
  const icon = NODE_ICON_MARKUP[type] ?? NODE_ICON_MARKUP.character;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none">
      ${buildShapeMarkup(family, accent)}
      ${icon}
    </svg>
  `;

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export function getAffinityChartNodeVisualSrc(node: Pick<AffinityChartNode, "family" | "imageUrl" | "type">) {
  return node.imageUrl ?? getAffinityChartNodePlaceholderDataUri(node.type, node.family);
}
